// 환경 키트 GLB 로더: 단일 메시의 지오메트리·베이스컬러·노멀 텍스처만 꺼낸다. 맵 사이에서 공유하고 한 번만 읽는다.
// 파일이 없거나 깨졌으면 null(그레이박스 유지). 파일을 public/assets에 넣으면 다음 실행에서 바로 쓰인다(목록 고정 아님).
import * as THREE from 'three/webgpu';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import type { KitKind } from './layout';
import { kitCacheKey, kitCandidates, type KitRegion } from './regionKits';
import { assetUrl as pwaAssetUrl } from '../../pwa/assetMap';

const FILES: Record<KitKind, string> = {
  wall: 'r1/kit_wall', wall_broken: 'r1/kit_wall_broken', pillar: 'r1/kit_pillar', pillar_broken: 'r1/kit_pillar_broken',
  arch: 'r1/kit_arch', stairs: 'r1/kit_stairs', statue: 'r1/kit_statue', banner: 'r1/kit_banner', brazier: 'r1/kit_brazier',
  rubble: 'r1/kit_rubble', rock: 'r1/kit_rock', tree: 'r1/kit_tree', crates: 'r1/kit_crates', mushrooms: 'r1/kit_mushrooms',
  tree_lo: 'r1/kit_tree_lo', rock_lo: 'r1/kit_rock_lo',
  v_house: 'village/v_house', v_lamppost: 'village/v_lamppost', v_gate: 'village/v_gate', v_shop: 'village/v_shop',
  v_fountain: 'village/v_fountain', v_forge: 'village/v_forge', v_questboard: 'village/v_questboard', v_trainer: 'village/v_trainer',
  // 에셋 파운드리 마을 소품(EXT_meshopt_compression + KHR_mesh_quantization, docs/ASSETS.md 7장)
  village_well: 'village/village_well', village_market_stall: 'village/village_market_stall',
  village_flower_planter: 'village/village_flower_planter', village_banner_pole: 'village/village_banner_pole',
  village_house_b: 'village/village_house_b',
};

export interface KitAsset {
  readonly kind: KitKind;
  /** position·normal·uv(+index)만 남긴 지오메트리(발이 원점, 정면 +Z) */
  readonly geometry: THREE.BufferGeometry;
  readonly map: THREE.Texture | null;
  readonly normalMap: THREE.Texture | null;
  /** 발광 텍스처(지역판 키트의 얼음 결정·포자 버섯·화로 숯, 베이스 컬러와 같은 UV) */
  readonly emissiveMap: THREE.Texture | null;
  /** 실제로 읽은 판: 지역판('r3'·'r4')이면 색 보정·발광 규칙이 달라진다. 'base' = r1·마을 기본 키트 */
  readonly region: KitRegion | 'base';
  /** bbox 치수(m) */
  readonly w: number;
  readonly h: number;
  readonly d: number;
}

export const assetUrl = (rel: string): string => pwaAssetUrl(rel);

const cache = new Map<string, Promise<KitAsset | null>>();
let loader: GLTFLoader | null = null;
let warned = false;

/** 읽은 텍스처 중 쓰지 않는 것(금속·거칠기)은 바로 버린다. */
function dropTexture(t: THREE.Texture | null | undefined): void {
  if (!t) return;
  const img = t.image as { close?: () => void } | undefined;
  img?.close?.();
  t.dispose();
}

/**
 * 양자화(정규화 정수)·인터리브 속성을 Float32로 푼다. 노드 배율을 곱하기 전에 해야 한다:
 * 정규화 Int16에 그대로 행렬을 적용하면 ±1 밖 값이 잘린다.
 */
function toFloat(g: THREE.BufferGeometry, name: string): void {
  const a = g.getAttribute(name) as THREE.BufferAttribute | THREE.InterleavedBufferAttribute | undefined;
  if (!a) return;
  const inter = (a as THREE.InterleavedBufferAttribute).isInterleavedBufferAttribute === true;
  if (!inter && a.array instanceof Float32Array && !a.normalized) return;
  const s = a.itemSize, n = a.count;
  const out = new Float32Array(n * s);
  for (let i = 0; i < n; i++) {
    out[i * s] = a.getX(i);
    if (s > 1) out[i * s + 1] = a.getY(i);
    if (s > 2) out[i * s + 2] = a.getZ(i);
    if (s > 3) out[i * s + 3] = a.getW(i);
  }
  g.setAttribute(name, new THREE.BufferAttribute(out, s));
}

async function loadOne(kind: KitKind, region: KitRegion | null): Promise<KitAsset | null> {
  const files = kitCandidates(kind, FILES[kind], region);
  for (let i = 0; i < files.length; i++) {
    const a = await loadFile(kind, files[i], i === files.length - 1);
    if (a) return { ...a, region: i === 0 && files.length > 1 && region ? region : 'base' };
  }
  return null;
}

async function loadFile(kind: KitKind, file: string, last: boolean): Promise<Omit<KitAsset, 'region'> | null> {
  try {
    loader ??= new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
    const gltf = await loader.loadAsync(assetUrl(file + '.glb'));
    let mesh: THREE.Mesh | null = null;
    gltf.scene.traverse((o) => {
      if (!mesh && (o as THREE.Mesh).isMesh) mesh = o as THREE.Mesh;
    });
    if (!mesh) throw new Error('메시 없음');
    const src = mesh as THREE.Mesh;
    src.updateWorldMatrix(true, false);
    const geometry = src.geometry.clone();
    for (const key of Object.keys(geometry.attributes)) {
      if (key !== 'position' && key !== 'normal' && key !== 'uv') geometry.deleteAttribute(key);
    }
    toFloat(geometry, 'position');
    toFloat(geometry, 'normal');
    toFloat(geometry, 'uv');
    geometry.applyMatrix4(src.matrixWorld);
    if (!geometry.getAttribute('normal')) geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    const bb = geometry.boundingBox!;
    const mat = src.material as THREE.MeshStandardMaterial;
    const map = mat.map ?? null;
    const normalMap = mat.normalMap ?? null;
    const emissiveMap = mat.emissiveMap ?? null;
    if (map) map.anisotropy = 4;
    const rough = mat.roughnessMap;
    dropTexture(rough);
    if (mat.metalnessMap && mat.metalnessMap !== rough) dropTexture(mat.metalnessMap);
    src.geometry.dispose();
    mat.dispose();
    return { kind, geometry, map, normalMap, emissiveMap, w: bb.max.x - bb.min.x, h: bb.max.y - bb.min.y, d: bb.max.z - bb.min.z };
  } catch (err) {
    // 지역판이 없으면 다음 후보(r1)로 조용히 넘어간다
    if (!last) return null;
    // 아직 만들어지지 않은 에셋은 정상 상황이라 한 번만 알린다
    if (!warned) console.info('환경 키트 일부를 읽지 못해 그레이박스로 대신한다(첫 항목: ' + kind + ')', err instanceof Error ? err.message : err);
    warned = true;
    return null;
  }
}

/** 키트 하나(맵 사이 공유). region이 있으면 지역판(r3/kit_x)을 먼저, 없으면 r1로 대신한다 */
export function loadKit(kind: KitKind, region: KitRegion | null = null): Promise<KitAsset | null> {
  const key = kitCacheKey(kind, region);
  let p = cache.get(key);
  if (!p) cache.set(key, (p = loadOne(kind, region)));
  return p;
}

const texCache = new Map<string, Promise<THREE.Texture | null>>();
/** 반복 타일 텍스처(sRGB, 반복 감기) */
export function loadTexture(rel: string, mirror = false): Promise<THREE.Texture | null> {
  let p = texCache.get(rel);
  if (!p) {
    p = new THREE.TextureLoader().loadAsync(assetUrl(rel)).then(
      (t) => {
        t.colorSpace = THREE.SRGBColorSpace;
        t.wrapS = t.wrapT = mirror ? THREE.MirroredRepeatWrapping : THREE.RepeatWrapping;
        t.anisotropy = 8;
        return t;
      },
      (err) => {
        console.info('환경 텍스처를 읽지 못했다: ' + rel, err instanceof Error ? err.message : err);
        return null;
      },
    );
    texCache.set(rel, p);
  }
  return p;
}

/** 진행률(0..1): 시작한 로드 중 끝난 비율 */
let started = 0, finished = 0;
export function track<T>(p: Promise<T>): Promise<T> {
  started++;
  return p.finally(() => { finished++; });
}
export function envProgress(): number {
  return started === 0 ? 0 : finished / started;
}

